import { useState, useEffect } from "react";
import { IconCheck, IconAlertTriangle } from "@tabler/icons-react";
import ColourPicker from "./ColourPicker";
import ModalShell from "../Support/ModalShell";
import Button from "../UI/Button/button";
import Input, { Select } from "../UI/FormInput/input";
import { getTeamApiError } from "./utils/apiError";
import { diffFields } from "../../utils/functions";

function toFormState(team) {
  return {
    name: team?.name || "",
    description: team?.description || "",
    managerId: team?.managerId?._id || team?.managerId || "",
    colour: team?.colour || "#1d4ed8",
  };
}

export default function EditTeamModal({ open, team, users, onClose, onSave }) {
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (team) {
      const state = toFormState(team);
      setInitial(state);
      setForm(state);
      setError(null);
    }
  }, [team]);

  if (!team || !form) return null;

  const setField = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError("Team name is required.");
      return;
    }
    const changed = diffFields(initial, form);
    if ("managerId" in changed && changed?.managerId === "") {
      changed.managerId = null; // explicit clear, not "unchanged"
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSave(team?._id, changed);
    } catch (err) {
      setError(getTeamApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Edit Team"
      footer={
        <>
          <Button variant="outlineBlue" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leftIcon={<IconCheck size={15} />}
            onClick={handleSubmit}
            loading={submitting}
          >
            Save Changes
          </Button>
        </>
      }
    >
      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          <IconAlertTriangle
            size={15}
            style={{ flexShrink: 0, marginTop: 1 }}
          />
          <span>
            {typeof error === "string"
              ? error
              : "Please check the form and try again."}
          </span>
        </div>
      )}

      <Input
        label="Team Name"
        id="editTeamName"
        placeholder="Team name"
        value={form.name}
        onChange={setField("name")}
      />
      <Input
        label="Description"
        id="editTeamDesc"
        placeholder="What does this team do?"
        value={form.description}
        onChange={setField("description")}
      />
      <Select
        label="Team Manager"
        id="editTeamManager"
        value={form.managerId}
        onChange={setField("managerId")}
      >
        <option value="">No manager</option>
        {users.map((u) => (
          <option key={u._id} value={u._id}>
            {u.name}
          </option>
        ))}
      </Select>
      <div>
        <label
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--text-secondary)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            display: "block",
            marginBottom: 5,
          }}
        >
          Team Colour
        </label>
        <ColourPicker
          value={form.colour}
          onChange={(c) => setForm((f) => ({ ...f, colour: c }))}
        />
      </div>
    </ModalShell>
  );
}
